import { useState } from 'react';
import { supabase, friendlyAuthError } from '../lib/supabase';

/**
 * Sign-in for the taskpane.
 *
 * Two methods, both completing INSIDE the pane:
 *
 *  • Password — `signInWithPassword`, nothing special.
 *
 *  • Email code — `signInWithOtp` + `verifyOtp`. This is the "magic link"
 *    mechanism, verified by the 6-digit code rather than by clicking the link.
 *    A taskpane is an iframe with no URL bar and no redirect target, so a
 *    clicked link opens the user's *browser* and the session lands somewhere
 *    the add-in cannot read. The code path uses the same Supabase email and
 *    keeps the session where it belongs. Requires `{{ .Token }}` in the magic
 *    link email template — see the README.
 *
 * `shouldCreateUser: false` on purpose: the add-in signs existing Flowza users
 * in, it is not a signup surface.
 */
export default function LoginPane({ onSignedIn }: { onSignedIn: () => void }) {
  const [mode, setMode] = useState<'password' | 'code'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const signInWithPassword = async () => {
    setBusy(true); setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) { setError(friendlyAuthError(error.message)); return; }
    onSignedIn();
  };

  const sendCode = async () => {
    setBusy(true); setError(null); setNotice(null);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: false },
    });
    setBusy(false);
    if (error) { setError(friendlyAuthError(error.message)); return; }
    setCodeSent(true);
    setNotice('Check your inbox for a 6-digit code.');
  };

  const verifyCode = async () => {
    setBusy(true); setError(null);
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: code.trim(),
      type: 'email',
    });
    setBusy(false);
    if (error) { setError(friendlyAuthError(error.message)); return; }
    onSignedIn();
  };

  const emailValid = /\S+@\S+\.\S+/.test(email.trim());

  return (
    <div className="pane">
      <header className="brand">
        <h1>Flowza Planner</h1>
        <p className="muted">Sign in to turn this email into a task.</p>
      </header>

      <div className="tabs" role="tablist">
        <button
          role="tab" aria-selected={mode === 'password'}
          className={mode === 'password' ? 'tab active' : 'tab'}
          onClick={() => { setMode('password'); setError(null); }}
        >Password</button>
        <button
          role="tab" aria-selected={mode === 'code'}
          className={mode === 'code' ? 'tab active' : 'tab'}
          onClick={() => { setMode('code'); setError(null); }}
        >Email code</button>
      </div>

      <label className="field">
        <span>Work email</span>
        <input
          type="email" value={email} autoComplete="username"
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com" disabled={busy || codeSent}
        />
      </label>

      {mode === 'password' ? (
        <>
          <label className="field">
            <span>Password</span>
            <input
              type="password" value={password} autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && emailValid && password) signInWithPassword(); }}
              disabled={busy}
            />
          </label>
          <button className="primary" onClick={signInWithPassword} disabled={busy || !emailValid || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </>
      ) : !codeSent ? (
        <button className="primary" onClick={sendCode} disabled={busy || !emailValid}>
          {busy ? 'Sending…' : 'Email me a code'}
        </button>
      ) : (
        <>
          <label className="field">
            <span>6-digit code</span>
            <input
              type="text" inputMode="numeric" maxLength={8} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter' && code.length >= 6) verifyCode(); }}
              placeholder="123456" disabled={busy} autoFocus
            />
          </label>
          <button className="primary" onClick={verifyCode} disabled={busy || code.length < 6}>
            {busy ? 'Verifying…' : 'Verify and sign in'}
          </button>
          <button
            className="link"
            onClick={() => { setCodeSent(false); setCode(''); setNotice(null); }}
            disabled={busy}
          >Use a different email</button>
        </>
      )}

      {notice && <p className="notice">{notice}</p>}
      {error && <p className="error" role="alert">{error}</p>}
    </div>
  );
}
